import { Injectable, NgZone } from "@angular/core";
import { ProjectConfigService } from "./project-config.service";
import { XtrasService } from "./xtras.service";
import { IframeCommunicationService } from "./iframe-comunication.service";
import { filter } from "rxjs";
import { NavigationEnd, Router } from "@angular/router";
import { HttpService } from "./http.service";
import { CYModalService } from "../components/modal/modal.service";
import { InformationModal } from "../components/modals/information/information.component";

declare let fbq: Function;

@Injectable({
  providedIn: 'root'
})
export class InitializeAppService {

  private resizeObserver: ResizeObserver | null = null;
  private lastUrl: string = '';

  constructor(
    private xServices: XtrasService,
    private projectConfig: ProjectConfigService,
    private iframeComm: IframeCommunicationService,
    private ngZone: NgZone,
    private router: Router,
    private httpService: HttpService,
    private modal: CYModalService,
  ) {}

  initializeApp() {
    let { keyMap, hasIframe, zendeskKey, facebookPixelKey, bgCustomRoutes, bgDarkRoutes } = this.projectConfig.getConfig();

    if(keyMap) {
      this.loadGoogleMaps(keyMap);
    }

    if(hasIframe) {
      document.body.classList.add('has__iframe');
      // this.setupIframeCommunication();
      this.startHeightMonitoring();
      this.setupUrlMonitoring();
    }

    if(zendeskKey) {
      this.loadZendesk(zendeskKey);
    }

    if(facebookPixelKey) {
      this.loadFacebookPixel(facebookPixelKey);
    }

    if(bgCustomRoutes.length > 0 || bgDarkRoutes.length > 0) {
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.ngZone.run(() => {
          this.updateAppClass(event.urlAfterRedirects);
        });
      });
    }

    this.getInformation();
  }

  private loadGoogleMaps(apiKey: string): void {
    this.xServices.loadInlineScriptBody(`
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
            v: "weekly",
            key: "${apiKey}"
        });
    `);
  }

  private loadZendesk(apiKey: string): void {
    this.xServices.loadScriptBody(`https://static.zdassets.com/ekr/snippet.js?key=${apiKey}`, false, { param: 'id', value: 'ze-snippet' });
  }

  private loadFacebookPixel(apiKey: string): void {
    // Cargar el código base del píxel (sin trackear PageView aún)
    this.xServices.loadInlineScriptHead(`
      !function(f,b,e,v,n,t,s)

      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?

      n.callMethod.apply(n,arguments):n.queue.push(arguments)};

      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';

      n.queue=[];t=b.createElement(e);t.async=!0;

      t.src=v;s=b.getElementsByTagName(e)[0];

      s.parentNode.insertBefore(t,s)}(window, document,'script',

      'https://connect.facebook.net/en_US/fbevents.js');

      fbq('init', '${apiKey}');
      // fbq('track', 'PageView');
    `);

    // Noscript fallback
    this.xServices.loadInlineScriptHead(`
      <img height="1" width="1" style="display:none"
      src="https://www.facebook.com/tr?id=${apiKey}&ev=PageView&noscript=1"
      />
    `, true);

    // Configurar tracking de PageView en cada cambio de ruta
    this.setupFacebookPixelTracking();
  }

  private setupFacebookPixelTracking(): void {
    // Trackear cada cambio de ruta
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // console.log('event', event.url);
      this.ngZone.run(() => {
        // Esperar un poco para asegurar que el píxel está cargado
        setTimeout(() => {
          if (typeof fbq === 'function') {
            console.log('trackPageView', event.url);
            this.trackPageView(event.url);
          }
        }, 100);
      });
    });
  }

  private trackPageView(url: string): void {
    fbq('track', 'PageView');

    fbq('trackCustom', 'ViewContent', {
      content_name: this.getPageName(url),
      content_category: this.getPageCategory(url),
      page_url: url
    });
  }

  private getPageName(url: string): string {
    // Extraer nombre de la página desde la URL
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(segment => segment.length > 0);

    if (segments.length === 0) return 'Home';
    return segments[segments.length - 1].replace('-', ' ').toUpperCase();
  }

  private getPageCategory(url: string): string {
    // Categorizar las páginas por sección
    if (url.includes('/login')) return 'SignIn';
    if (url.includes('/register')) return 'SignUp';
    if (url.includes('/forgot-password')) return 'ForgotPassword';
    if (url.includes('/payment')) return 'Payment';
    if (url.includes('/success')) return 'PaymentSuccess';
    if (url.includes('/payment-success')) return 'PaymentVoucher';
    if (url.includes('/account')) return 'Account';
    if (url.includes('/tickets-saved')) return 'TicketsSaved';
    if (url.includes('/ticket')) return 'Event';
    if (url.includes('/sections-selector')) return 'SectionsSelector';
    if (url.includes('/search')) return 'Search';
    if (url.includes('/privacy-policy')) return 'PrivacyPolicy';
    if (url === '/' || url === '' || url === '/home') return 'Home';
    return 'General';
  }

  startHeightMonitoring(elementId: string = 'content__site'): void {
    const element = document.body;

    this.resizeObserver = new ResizeObserver(entries => {
      this.ngZone.run(() => {
        const height = entries[0].contentRect.height;
        this.iframeComm.sendToParent({
          type: 'CONTENT_HEIGHT_UPDATE',
          height: height,
          elementId: elementId
        });
      });
    });

    this.resizeObserver.observe(element);
  }

  stopHeightMonitoring(): void {
    this.resizeObserver?.disconnect();
  }

  private setupUrlMonitoring(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.ngZone.run(() => {
        this.updateAppClass(event.urlAfterRedirects);
        const currentUrl = this.getCleanUrl(event.url);

        if (currentUrl !== this.lastUrl) {
          this.lastUrl = currentUrl;
          // Enviar cambio de URL al padre
          this.iframeComm.sendToParent({
            type: 'CHANGE_PAGE',
            url: currentUrl,
            timestamp: Date.now()
          });
        }
      });
    });
  }

  private getCleanUrl(fullUrl: string): string {
    return fullUrl.split('?')[0].split('#')[0];
  }

  public getCurrentCleanUrl(): string {
    return this.getCleanUrl(this.router.url);
  }

  private updateAppClass(url: string) {
    console.log(url);
    const webApp = document.querySelector('#cy-webapp');
    if (!webApp) return;

    webApp.classList.remove('bg_dark');
    webApp.classList.remove('bg_custom');

    const bgDarkRoutes = this.projectConfig.getConfig().bgDarkRoutes;
    // console.log(bgDarkRoutes);
    const bgCustomRoutes = this.projectConfig.getConfig().bgCustomRoutes;
    console.log(bgCustomRoutes);

    setTimeout(() => {
      const bgDark = bgDarkRoutes.some((route: string) => url.includes(route));
      const bgCustom = bgCustomRoutes.some((route: string) => url.includes(route));

      if (bgDark) {
        webApp.classList.add('bg_dark');
      } else if (bgCustom) {
        webApp.classList.add('bg_custom');
      }
    });
  }

  private getInformation() {
    this.httpService.get_request('tickets/website/alert/home?source=web').subscribe({
      next: (resp: any) => {
        console.log(resp)
        if(resp?.status && (resp?.data && resp?.data?.alert)) {
          let data: any = resp.data;
          this.modal.open(InformationModal, {
            data: {
              data: data?.alert
            },
            width: '600px'
          })
        }
      },
      error: (error) => {},
    })
  }
}
